import {useEffect, useState} from "react";
import {Check, Clock3, ExternalLink, X} from "lucide-react";
import {EnsAlertNotification} from "../../../store/features/ens/types";

const parseTimestamp = (value: string | null): number | null => {
    if (value == null) return null;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
};

const formatTimeRemaining = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes.toString().padStart(2, '0')}m`;
};

interface Props {
    notification: EnsAlertNotification;
    canConfirm: boolean;
    confirmed: boolean;
    confirming: boolean;
    error: string | null;
    desktopPermission: NotificationPermission | 'unsupported';
    onConfirm: () => void;
    onEnableDesktopNotifications: () => void;
    onOpen: () => void;
    onDismiss: () => void;
}

export const EnsNotificationToast = ({
    notification,
    canConfirm,
    confirmed,
    confirming,
    error,
    desktopPermission,
    onConfirm,
    onEnableDesktopNotifications,
    onOpen,
    onDismiss,
}: Props) => {
    const nextLevelTimestamp = parseTimestamp(notification.nextLevelScheduledAt);
    const occurredAtTimestamp = parseTimestamp(notification.occurredAt);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (confirmed || nextLevelTimestamp == null || nextLevelTimestamp <= Date.now()) return;

        const timer = window.setInterval(() => {
            const currentTime = Date.now();
            setNow(currentTime);
            if (currentTime >= nextLevelTimestamp) window.clearInterval(timer);
        }, 1_000);

        return () => window.clearInterval(timer);
    }, [confirmed, nextLevelTimestamp]);

    const timeRemaining = nextLevelTimestamp == null
        ? null
        : Math.max(0, Math.ceil((nextLevelTimestamp - now) / 1_000));
    const countdownDuration = nextLevelTimestamp != null && occurredAtTimestamp != null
        ? Math.max(1, nextLevelTimestamp - occurredAtTimestamp)
        : 1;
    const countdownProgress = nextLevelTimestamp == null
        ? 0
        : Math.min(100, Math.max(0, ((nextLevelTimestamp - now) / countdownDuration) * 100));
    const countdownUrgent = timeRemaining != null && timeRemaining < 10;

    if (confirmed) {
        return (
            <div
                role="status"
                className="overflow-hidden rounded-[14px] border border-green-300 bg-surface p-5 shadow-[0_12px_36px_rgba(0,0,0,0.16)]"
            >
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-700 shadow-md">
                        <Check size={22} strokeWidth={3} className="text-white"/>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-base font-bold text-ink">Receipt confirmed</div>
                        <div className="mt-1 text-sm leading-5 text-green-700">
                            Further escalation for your account has stopped.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="overflow-hidden rounded-[14px] border border-line bg-surface shadow-[0_12px_36px_rgba(0,0,0,0.18)]"
        >
            <div className="flex items-center justify-between bg-gradient-to-r from-violet-600 to-purple-500 px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-white"/>
                    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-white">
                        Action required
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-white/85">L1 · Push notification</span>
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label="Dismiss emergency notification"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-white/80 hover:bg-white/15 hover:text-white"
                    >
                        <X size={15}/>
                    </button>
                </div>
            </div>

            <div className="p-4">
                <button type="button" onClick={onOpen} className="block w-full text-left">
                    <div
                        id={`ens-notification-title-${notification.notificationId}`}
                        className="text-[15px] font-bold leading-5 text-ink"
                    >
                        {notification.name}
                    </div>
                    <div
                        id={`ens-notification-message-${notification.notificationId}`}
                        className="mt-1.5 max-h-24 overflow-y-auto whitespace-pre-line text-[13px] leading-5 text-ink-secondary"
                    >
                        {notification.message}
                    </div>
                </button>

                {timeRemaining != null && (
                    <div
                        className={`mt-4 rounded-[10px] border p-3 ${
                            countdownUrgent
                                ? 'border-red-200 bg-red-50'
                                : 'border-amber-200 bg-amber-50'
                        }`}
                    >
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                            <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.04em] ${
                                countdownUrgent ? 'text-red-700' : 'text-amber-700'
                            }`}>
                                <Clock3 size={13}/>
                                <span role="timer" aria-live="off">
                                    {timeRemaining > 0
                                        ? `Escalates in ${formatTimeRemaining(timeRemaining)}`
                                        : 'Escalating now'}
                                </span>
                            </div>
                            <span className={`shrink-0 text-[11px] font-semibold ${
                                countdownUrgent ? 'text-red-700' : 'text-amber-700'
                            }`}>
                                &rarr; Next stage
                            </span>
                        </div>
                        <div className={`h-1 overflow-hidden rounded-sm ${
                            countdownUrgent ? 'bg-red-200' : 'bg-amber-200'
                        }`}>
                            <div
                                className={`h-full rounded-sm transition-[width] duration-1000 ease-linear ${
                                    countdownUrgent ? 'bg-red-600' : 'bg-amber-600'
                                }`}
                                style={{width: `${countdownProgress}%`}}
                            />
                        </div>
                    </div>
                )}

                <div className="mt-4 rounded-[10px] border border-line bg-surface-muted p-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted">
                        {canConfirm ? 'By confirming, you will' : 'Emergency alert'}
                    </div>
                    <div className="mt-2 flex items-start gap-2 text-xs leading-[18px] text-ink">
                        <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-violet-500">
                            <Check size={9} strokeWidth={3.5} className="text-white"/>
                        </span>
                        {canConfirm
                            ? 'Stop further escalation for your recipient account and record the acknowledgement.'
                            : 'Open the alert details to review recipients and the current escalation state.'}
                    </div>
                </div>

                {error && (
                    <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {error}
                    </div>
                )}

                <div className="mt-4 flex">
                    <button
                        type="button"
                        onClick={canConfirm ? onConfirm : onOpen}
                        disabled={confirming}
                        className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-gradient-to-r from-violet-600 to-purple-500 px-3 text-[13px] font-bold text-white shadow-md hover:from-violet-700 hover:to-purple-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {canConfirm ? <Check size={15} strokeWidth={2.5}/> : <ExternalLink size={14}/>}
                        {confirming
                            ? 'Confirming…'
                            : canConfirm
                                ? 'Confirm receipt'
                                : 'View alert details'}
                    </button>
                </div>

                {desktopPermission === 'default' && (
                    <button
                        type="button"
                        onClick={onEnableDesktopNotifications}
                        className="mt-3 w-full text-center text-xs font-semibold text-brand-ink hover:text-brand-ink"
                    >
                        Enable desktop notifications for alerts received in the background
                    </button>
                )}
                {desktopPermission === 'denied' && (
                    <div className="mt-3 text-center text-xs leading-4 text-ink-muted">
                        Desktop notifications are blocked in the browser. In-page alerts will remain available.
                    </div>
                )}
            </div>
        </div>
    );
};
